// Environmental record detail (ISO 14001). Inline-editable fields and a delete control. A record
// with attached waste consignment notes (EPA 1990 Duty of Care statutory evidence) is ARCHIVED, not
// hard-deleted (the delete button reads "Archive record" and the record is kept, hidden from the
// default list, reactivatable); a record with no notes hard-deletes. Office/admin.
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, ApiError, type EnvRecordItem, type EnvironmentalRecordType, type WasteNoteItem } from "../../lib/api";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { RECORD_TYPE_LABEL } from "./environmentalUi";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { RecordHeader } from "../../components/RecordHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { SiteLiftFields } from "./EnvironmentalRecordsPage";

const inputCls = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

export function EnvironmentalRecordDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState<EnvRecordItem | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setState("loading");
      try {
        setRecord(await api.environmentalRecords.get(id));
        setState("ready");
      } catch (err) {
        if (!opts?.silent) setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      }
    },
    [id],
  );
  useEffect(() => {
    void load();
  }, [load]);

  if (state === "loading") return <Loader shape="panel" className="p-6" />;
  if (state === "notfound") return <p className="p-6 text-center text-ele-body">That record could not be found.</p>;
  if (state === "error" || !record) return <p role="alert" className="p-6 text-center text-ele-strong">Could not load this record. Please try again.</p>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <RecordHeader
        className="mb-4"
        title={record.ref}
        backTo="/office/environmental-records"
        backLabel="Back to environmental records"
        status={
          <>
            <StatusPill word={RECORD_TYPE_LABEL[record.recordType] ?? record.recordType} tone="grey" />
            {record.archivedAt && <StatusPill word="Archived" tone="amber" variant="outline" />}
            <span className="text-sm text-ele-body">{formatDate(record.date)}</span>
          </>
        }
      />

      <EditPanel record={record} onSaved={() => load({ silent: true })} />
      <WasteNotesSection recordId={record.id} />
      {record.archivedAt ? (
        <ReactivateSection record={record} onReactivated={() => load({ silent: true })} />
      ) : (
        <DeleteSection
          record={record}
          onDeleted={() => navigate("/office/environmental-records")}
          onArchived={() => load({ silent: true })}
        />
      )}
    </div>
  );
}

function EditPanel({ record, onSaved }: { record: EnvRecordItem; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState(seed(record));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(seed(record));
  }, [record]);

  const set = (name: keyof ReturnType<typeof seed>) => (e: { target: { value: string } }) => {
    setForm((f) => ({ ...f, [name]: e.target.value }));
    setSaved(false);
  };

  async function save(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.environmentalRecords.update(record.id, {
        recordType: form.recordType as EnvironmentalRecordType,
        date: form.date || undefined,
        description: form.description.trim() || undefined,
        quantity: form.quantity.trim() ? Number(form.quantity) : null,
        unit: form.unit.trim() || null,
        disposalMethod: form.disposalMethod.trim() || null,
        contractor: form.contractor.trim() || null,
        contractorRegistrationRef: form.contractorRegistrationRef.trim() || null,
        wasteCode: form.wasteCode.trim() || null,
        location: form.location.trim() || null,
        siteId: form.siteId || null,
        assetId: form.assetId || null,
        severity: form.severity.trim() || null,
        actionTaken: form.actionTaken.trim() || null,
        status: form.status.trim() || null,
        notes: form.notes.trim() || null,
      });
      setSaved(true);
      await onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
        <label>
          <span className={labelCls}>Type</span>
          <select aria-label="Type" value={form.recordType} onChange={set("recordType")} className={inputCls}>
            {(Object.keys(RECORD_TYPE_LABEL) as EnvironmentalRecordType[]).map((t) => (
              <option key={t} value={t}>
                {RECORD_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className={labelCls}>Date</span>
          <input aria-label="Date" type="date" value={form.date} onChange={set("date")} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Description</span>
          <textarea aria-label="Description" value={form.description} onChange={set("description")} rows={2} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Quantity</span>
          <input aria-label="Quantity" type="number" step="0.01" min="0" value={form.quantity} onChange={set("quantity")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Unit</span>
          <input aria-label="Unit" value={form.unit} onChange={set("unit")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Disposal method</span>
          <input aria-label="Disposal method" value={form.disposalMethod} onChange={set("disposalMethod")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Contractor</span>
          <input aria-label="Contractor" value={form.contractor} onChange={set("contractor")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Contractor's waste carrier registration ref</span>
          <input
            aria-label="Contractor's waste carrier registration reference"
            value={form.contractorRegistrationRef}
            onChange={set("contractorRegistrationRef")}
            placeholder="If ELE holds it on file"
            className={inputCls}
          />
        </label>
        <label>
          <span className={labelCls}>Waste code (EWC)</span>
          <input aria-label="Waste code" value={form.wasteCode} onChange={set("wasteCode")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Location</span>
          <input aria-label="Location" value={form.location} onChange={set("location")} className={inputCls} />
        </label>
        <SiteLiftFields
          siteId={form.siteId}
          assetId={form.assetId}
          onSiteChange={(siteId) => setForm((f) => ({ ...f, siteId, assetId: "" }))}
          onAssetChange={(assetId) => setForm((f) => ({ ...f, assetId }))}
        />
        <label>
          <span className={labelCls}>Severity</span>
          <input aria-label="Severity" value={form.severity} onChange={set("severity")} className={inputCls} />
        </label>
        <label>
          <span className={labelCls}>Status</span>
          <input aria-label="Record status" value={form.status} onChange={set("status")} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Action taken</span>
          <textarea aria-label="Action taken" value={form.actionTaken} onChange={set("actionTaken")} rows={2} className={inputCls} />
        </label>
        <label className="sm:col-span-2">
          <span className={labelCls}>Notes</span>
          <textarea aria-label="Notes" value={form.notes} onChange={set("notes")} rows={2} className={inputCls} />
        </label>
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
          {saved && (
            <span role="status" className="text-sm text-ele-green">
              Saved.
            </span>
          )}
        </div>
      </form>
      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </section>
  );
}

// Waste consignment note uploads (EPA 1990 Duty of Care evidence). Accepts PDF / JPG / PNG up
// to 10MB; the file is filed in the Compliance Drive folder and viewed via the authenticated
// download proxy. Per-file delete uses the shared confirmation modal (the locked delete rule).
function WasteNotesSection({ recordId }: { recordId: string }) {
  const [notes, setNotes] = useState<WasteNoteItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<WasteNoteItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setNotes(await api.environmentalRecords.wasteNotes(recordId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not load the consignment notes.");
    }
  }, [recordId]);
  useEffect(() => {
    void load();
  }, [load]);

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-selected later
    if (!file) return;
    setError(null);
    const allowed = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) {
      setError("Upload a PDF, JPG or PNG.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("That file is over 10MB. Please upload a smaller one.");
      return;
    }
    setUploading(true);
    try {
      await api.environmentalRecords.uploadWasteNote(recordId, file, file.name);
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.code === "drive_not_connected"
            ? "Google Drive is not connected, so the file could not be filed."
            : err.message || err.code
          : "Could not upload the file. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setError(null);
    try {
      await api.environmentalRecords.deleteWasteNote(recordId, pendingDelete.id);
      setPendingDelete(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not delete the file.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
      <div className="mb-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="font-heading text-lg text-ele-blue">Waste consignment notes</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? "Uploading..." : "Upload note"}
          </Button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          className="hidden"
          onChange={onPick}
          aria-label="Upload waste consignment note"
        />
      </div>
      <p className="mb-3 text-xs text-ele-body">Evidence of waste transfer (Duty of Care). Accepted: PDF, JPG, PNG, up to 10MB.</p>
      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      {notes === null ? (
        <Loader label="Loading the consignment notes..." shape="inline" />
      ) : notes.length === 0 ? (
        <p className="text-sm text-ele-body opacity-70">No consignment notes uploaded yet.</p>
      ) : (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li key={n.id} className="flex flex-wrap items-center justify-between gap-3 rounded-ele border border-ele-purple-accent bg-ele-inset px-3 py-2 text-sm">
              <span className="min-w-0 break-all text-ele-strong">{n.filename}</span>
              <span className="flex items-center gap-3">
                <span className="text-xs text-ele-body">{formatDate(n.uploadedAt)}</span>
                <a
                  href={api.environmentalRecords.wasteNoteDownloadHref(recordId, n.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-menu text-xs tracking-wide text-ele-blue underline"
                >
                  View
                </a>
                <button type="button" onClick={() => setPendingDelete(n)} className="font-menu text-xs tracking-wide text-ele-danger underline">
                  Delete
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDeleteModal
        open={pendingDelete !== null}
        variant="destructive"
        title="Delete this consignment note?"
        message="This file will be permanently removed from Drive and from the record. This cannot be undone."
        confirmLabel="Delete note"
        busy={deleting}
        error={null}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />
    </section>
  );
}

function DeleteSection({ record, onDeleted, onArchived }: { record: EnvRecordItem; onDeleted: () => void; onArchived: () => void }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A record with attached waste consignment notes is statutory Duty of Care evidence: the delete
  // ARCHIVES it (mirrors the server envRecordDeleteMode). With no notes it is a permanent hard delete.
  const willArchive = record.wasteNoteCount > 0;

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.environmentalRecords.remove(record.id);
      // Belt and braces: if the server ever answers a 200 with archived:true, treat it as an archive.
      if (res.archived) {
        setBusy(false);
        setOpen(false);
        onArchived();
        return;
      }
      onDeleted();
    } catch (err) {
      // The server BLOCKS a hard delete of a record with attached statutory evidence with a 409 and
      // archives it instead; surface that as an archive (reload so the Archived state and Reactivate
      // show), never as an error.
      if (err instanceof ApiError && err.status === 409) {
        setBusy(false);
        setOpen(false);
        onArchived();
        return;
      }
      setError(err instanceof ApiError ? err.message || err.code : "Could not delete this record.");
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ele border border-ele-hairline bg-ele-surface-soft p-5">
      <Button
        variant="destructive"
        size="sm"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        {willArchive ? "Archive record" : "Delete record"}
      </Button>
      <ConfirmDeleteModal
        open={open}
        variant={willArchive ? "archive" : "destructive"}
        title={willArchive ? "Archive this record?" : "Delete this record?"}
        message={
          willArchive
            ? "This record has waste consignment notes attached as statutory Duty of Care evidence (Environmental Protection Act 1990), so it cannot be permanently deleted. It will be archived and hidden from the default list, and you can reactivate it later."
            : "This environmental record will be permanently deleted. This cannot be undone."
        }
        confirmLabel={willArchive ? "Archive record" : "Delete record"}
        busy={busy}
        error={error}
        onConfirm={confirm}
        onCancel={() => {
          if (!busy) {
            setOpen(false);
            setError(null);
          }
        }}
      />
    </section>
  );
}

function ReactivateSection({ record, onReactivated }: { record: EnvRecordItem; onReactivated: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function reactivate() {
    setBusy(true);
    setError(null);
    try {
      await api.environmentalRecords.reactivate(record.id);
      onReactivated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not reactivate this record.");
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ele border border-amber-500 bg-amber-500/10 p-5">
      <p className="mb-3 text-sm text-amber-300">
        This record is archived because it has waste consignment notes attached as statutory Duty of Care
        evidence (Environmental Protection Act 1990). It is kept for retention and hidden from the default list.
      </p>
      {error && (
        <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      <button
        onClick={reactivate}
        disabled={busy}
        className="rounded-ele bg-amber-500 px-4 py-2 font-heading text-sm tracking-wide text-ele-bg disabled:opacity-50"
      >
        {busy ? "Reactivating..." : "Reactivate record"}
      </button>
    </section>
  );
}

function seed(r: EnvRecordItem) {
  return {
    recordType: r.recordType as string,
    date: r.date ? r.date.slice(0, 10) : "",
    description: r.description,
    quantity: r.quantity != null ? String(r.quantity) : "",
    unit: r.unit ?? "",
    disposalMethod: r.disposalMethod ?? "",
    contractor: r.contractor ?? "",
    contractorRegistrationRef: r.contractorRegistrationRef ?? "",
    wasteCode: r.wasteCode ?? "",
    location: r.location ?? "",
    siteId: r.siteId ?? "",
    assetId: r.assetId ?? "",
    severity: r.severity ?? "",
    actionTaken: r.actionTaken ?? "",
    status: r.status ?? "",
    notes: r.notes ?? "",
  };
}
